import {
  type GachaEvmBuybackInput,
  type GachaEvmBuybackQuote,
  gachaEvmBuybackAbi,
} from "@anaconda/core/gacha/evm-buyback"
import {
  decodeAbiParameters,
  decodeFunctionData,
  encodeFunctionData,
  parseAbi,
  parseAbiParameters,
  size,
  sliceHex,
  zeroHash,
} from "viem"

// MetaMask v1.3.0 Base Sepolia deployment:
// https://github.com/MetaMask/delegation-framework/blob/main/documents/Deployments.md
const delegationManager = "0xdb9b1e94b5b69df7e401ddbede43491141047db3"
const delegationManagerAbi = parseAbi([
  "function redeemDelegations(bytes[] permissionContexts, bytes32[] modes, bytes[] executionCallDatas)",
])
// Tuple order follows delegation-framework@v1.3.0/src/utils/Types.sol.
const delegationParameters = parseAbiParameters(
  "(address delegate, address delegator, bytes32 authority, (address enforcer, bytes terms, bytes args)[] caveats, uint256 salt, bytes signature)[]",
)

type BuybackTransaction = {
  from: string
  to: string | null
  input: `0x${string}`
}

export function isGachaEvmBuybackTransaction(
  transaction: BuybackTransaction,
  input: GachaEvmBuybackInput,
  quote: GachaEvmBuybackQuote,
) {
  const expectedData = encodeFunctionData({
    abi: gachaEvmBuybackAbi,
    functionName: "sellBack",
    args: [
      quote.paymentToken,
      BigInt(quote.tokenId),
      BigInt(quote.refundAmountBase),
      BigInt(quote.deadline),
      BigInt(quote.quoteId),
      quote.signature,
    ],
  }).toLowerCase()
  const owner = input.playerAddress.toLowerCase()
  const vault = quote.vault.toLowerCase()
  if (
    transaction.from.toLowerCase() === owner &&
    transaction.to?.toLowerCase() === vault &&
    transaction.input.toLowerCase() === expectedData
  )
    return true

  if (
    input.chainId !== 84532 ||
    transaction.to?.toLowerCase() !== delegationManager
  )
    return false

  try {
    const decoded = decodeFunctionData({
      abi: delegationManagerAbi,
      data: transaction.input,
    })
    const [contexts, modes, executions] = decoded.args
    if (
      contexts.length !== 1 ||
      modes.length !== 1 ||
      executions.length !== 1 ||
      modes[0] !== zeroHash
    )
      return false

    const [delegations] = decodeAbiParameters(delegationParameters, contexts[0])
    // The manager executes through the root delegator, or msg.sender for an
    // empty chain. Never attribute the relayer's outer address to the sale.
    const account = delegations.at(-1)?.delegator ?? transaction.from
    if (account.toLowerCase() !== owner) return false

    const execution = executions[0]
    if (size(execution) < 52) return false
    // ERC-7579 single-call layout: target (20), value (32), then calldata.
    return (
      sliceHex(execution, 0, 20).toLowerCase() === vault &&
      BigInt(sliceHex(execution, 20, 52)) === BigInt(0) &&
      sliceHex(execution, 52).toLowerCase() === expectedData
    )
  } catch {
    return false
  }
}
